import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { balanceApi, BalanceEntry, PaidOutEntry, BusserTipEntry, EventServerBartenderTipEntry, NegativeCashOutEntry, RegisterEntry, BarTipEntry, ExpoTipEntry } from '../services/balanceService.ts';
import { employeeApi, Employee } from '../services/employeeService.ts';
import { tipoutApi } from '../services/tipoutService.ts';

interface BalanceRow {
  id: string;
  employeeId: number;
  employeeName: string;
  type: 'server' | 'host' | 'bar' | 'manager';
  cashDue: number;
  netSales: number;
}

// Fixed registers tracked on the New Balance form, each with a standard default
// (starting/float) amount; the only thing entered day-to-day is the close-out amount.
const REGISTER_DEFAULTS: Array<{ key: string; label: string; defaultAmount: number }> = [
  { key: 'bar_am', label: 'Bar AM', defaultAmount: 150 },
  { key: 'bar_pm', label: 'Bar PM', defaultAmount: 150 },
  { key: 'host', label: 'Host', defaultAmount: 250 },
  { key: 'keno', label: 'Keno', defaultAmount: 400 },
];

const NewBalancePage: React.FC = () => {
  const { sessionToken } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const [searchParams] = useSearchParams();
  
  // Current date state
  const [currentDate, setCurrentDate] = useState<string>('');
  const [dateLoading, setDateLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoadRef = useRef(true);

  // Day's total net sales (AM+PM daily logs only, not events) from the Tip Out Log,
  // compared against this page's own AM+PM net sales total to catch data-entry
  // mismatches between the two pages.
  const [tipOutNetSalesTotal, setTipOutNetSalesTotal] = useState(0);
  const [netSalesMismatchMessage, setNetSalesMismatchMessage] = useState<string | null>(null);
  const netSalesMismatchAlertedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  // Employees data
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  // Table data
  const [amData, setAmData] = useState<BalanceRow[]>([]);
  const [pmData, setPmData] = useState<BalanceRow[]>([]);
  const [paidOuts, setPaidOuts] = useState<PaidOutEntry[]>([]);
  const [busserTips, setBusserTips] = useState<BusserTipEntry[]>([]);
  const [eventServersBartenderTips, setEventServersBartenderTips] = useState<EventServerBartenderTipEntry[]>([]);
  const [negativeCashOuts, setNegativeCashOuts] = useState<NegativeCashOutEntry[]>([]);
  const [barTips, setBarTips] = useState<BarTipEntry[]>([]);
  const [expoTips, setExpoTips] = useState<ExpoTipEntry[]>([]);
  // Keyed by REGISTER_DEFAULTS[].key; each value is the day's close-out amount (as text,
  // same free-typing pattern as the other dollar fields on this page).
  const [registerForm, setRegisterForm] = useState<Record<string, string>>(
    REGISTER_DEFAULTS.reduce((acc, r) => ({ ...acc, [r.key]: '' }), {} as Record<string, string>)
  );
  // Keyed by REGISTER_DEFAULTS[].key; each value is the employeeId assigned to that register.
  const [registerEmployeeForm, setRegisterEmployeeForm] = useState<Record<string, string>>(
    REGISTER_DEFAULTS.reduce((acc, r) => ({ ...acc, [r.key]: '' }), {} as Record<string, string>)
  );

  // Form states
  const [amForm, setAmForm] = useState({ employeeId: '', type: 'server' as const, cashDue: '', netSales: '' });
  const [pmForm, setPmForm] = useState({ employeeId: '', type: 'server' as const, cashDue: '', netSales: '' });

  // Editing states
  const [editingAmRow, setEditingAmRow] = useState<string | null>(null);
  const [editingPmRow, setEditingPmRow] = useState<string | null>(null);

  // Temp edit data
  const [tempAmData, setTempAmData] = useState<Partial<BalanceRow> | null>(null);
  const [tempPmData, setTempPmData] = useState<Partial<BalanceRow> | null>(null);

  // Fetch employees
  const fetchEmployees = async () => {
    if (!sessionToken) return;
    
    try {
      setEmployeesLoading(true);
      const response = await employeeApi.getEmployees({ page: 1, limit: 1000 }, sessionToken);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      showError('Failed to fetch employees');
    } finally {
      setEmployeesLoading(false);
    }
  };

  // Fetch current date from API
  const fetchCurrentDate = async () => {
    if (!sessionToken) return;
    
    try {
      setDateLoading(true);
      const response = await balanceApi.getCurrentDate(sessionToken);
      setCurrentDate(response.date);
      return response.date;
    } catch (error) {
      console.error('Error fetching current date:', error);
      showError('Failed to fetch current date');
      return null;
    } finally {
      setDateLoading(false);
    }
  };

  // Format date string (YYYY-MM-DD) to locale string
  // Parse the date components and create a date in local timezone to avoid timezone shift
  const formatDateString = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Fetch the day's AM+PM daily (non-event) net sales total from the Tip Out Log,
  // for comparison against this page's own net sales total.
  const fetchTipOutNetSales = async (date: string) => {
    if (!sessionToken || !date) return;
    try {
      const logs = await tipoutApi.getDateLogs(sessionToken, date);
      const dailyLogs = logs.filter(log => log.eventType === 'daily');
      setTipOutNetSalesTotal(dailyLogs.reduce((sum, log) => sum + (Number(log.netSales) || 0), 0));
    } catch (error) {
      console.error('Error fetching tip out net sales:', error);
      setTipOutNetSalesTotal(0);
    }
  };

  // Fetch balance data
  const fetchBalanceData = async (date: string) => {
    if (!sessionToken || !date) return;

    // The data this fetch loads is from the server, not a user edit - don't auto-save it.
    isInitialLoadRef.current = true;
    fetchTipOutNetSales(date);

    try {
      setDataLoading(true);
      const data = await balanceApi.getBalanceByDate(sessionToken, date);
      
      // Convert balance data to rows
      const amRows: BalanceRow[] = data.balance
        .filter(b => b.shift === 'AM')
        .map((b, idx) => ({
          id: `am-${b.id || idx}`,
          employeeId: b.employeeId,
          employeeName: employees.find(e => e.id === b.employeeId)?.name || `Employee ${b.employeeId}`,
          type: b.type,
          cashDue: Number(b.cashDue) || 0,
          netSales: Number(b.netSales) || 0,
        }));
      
      const pmRows: BalanceRow[] = data.balance
        .filter(b => b.shift === 'PM')
        .map((b, idx) => ({
          id: `pm-${b.id || idx}`,
          employeeId: b.employeeId,
          employeeName: employees.find(e => e.id === b.employeeId)?.name || `Employee ${b.employeeId}`,
          type: b.type,
          cashDue: Number(b.cashDue) || 0,
          netSales: Number(b.netSales) || 0,
        }));

      setAmData(amRows);
      setPmData(pmRows);
      setPaidOuts(data.paid_outs || []);
      setBusserTips(data.busser_tips || []);
      setEventServersBartenderTips(data.event_servers_bartender_tips || []);
      setNegativeCashOuts(data.negative_cash_outs || []);
      setBarTips(data.bar_tips || []);
      setExpoTips(data.expo_tips || []);

      const registerMap = REGISTER_DEFAULTS.reduce((acc, r) => ({ ...acc, [r.key]: '' }), {} as Record<string, string>);
      const registerEmployeeMap = REGISTER_DEFAULTS.reduce((acc, r) => ({ ...acc, [r.key]: '' }), {} as Record<string, string>);
      (data.register || []).forEach((r: RegisterEntry) => {
        registerMap[r.registerType] = String(Number(r.closeOutAmount) || 0);
        registerEmployeeMap[r.registerType] = r.employeeId ? String(r.employeeId) : '';
      });
      setRegisterForm(registerMap);
      setRegisterEmployeeForm(registerEmployeeMap);
    } catch (error) {
      console.error('Error fetching balance data:', error);
      // Don't show error if no data exists for the date
    } finally {
      setDataLoading(false);
    }
  };

  // Shift a YYYY-MM-DD date string by a number of days without local-timezone drift.
  const shiftDate = (dateStr: string, days: number): string => {
    const d = new Date(`${dateStr}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  };

  // Used by the Prev/Next buttons on the Cash Registers table to browse the whole
  // form's data a day at a time (the whole New Balance form is scoped to one date).
  const handleDateNav = (direction: 'prev' | 'next') => {
    if (!currentDate || dateLoading || dataLoading) return;
    const newDate = shiftDate(currentDate, direction === 'prev' ? -1 : 1);
    setCurrentDate(newDate);
    fetchBalanceData(newDate);
  };

  // Flag a Net Sales mismatch between this page's AM+PM total and the Tip Out Log's
  // AM+PM daily total for the same date - outside a $5 tolerance band either way.
  // Skipped entirely while this page's total is still $0 (nothing entered yet), so an
  // empty day doesn't falsely alert against stale/leftover Tip Out Log data.
  useEffect(() => {
    if (dataLoading || !currentDate) return;
    const amNetSales = amData.reduce((sum, row) => sum + (Number(row.netSales) || 0), 0);
    const pmNetSales = pmData.reduce((sum, row) => sum + (Number(row.netSales) || 0), 0);
    const newBalanceNetSales = amNetSales + pmNetSales;
    const diff = newBalanceNetSales - tipOutNetSalesTotal;
    const mismatchKey = `${currentDate}:${newBalanceNetSales.toFixed(2)}:${tipOutNetSalesTotal.toFixed(2)}`;

    if (newBalanceNetSales > 0 && (diff < -5 || diff > 5)) {
      if (netSalesMismatchAlertedKeyRef.current !== mismatchKey) {
        netSalesMismatchAlertedKeyRef.current = mismatchKey;
        setNetSalesMismatchMessage('The net sales amounts in the tip-out log and New Balance do not match.');
      }
    } else {
      netSalesMismatchAlertedKeyRef.current = null;
    }
  }, [amData, pmData, tipOutNetSalesTotal, dataLoading, currentDate]);

  // Load data on mount
  useEffect(() => {
    if (sessionToken) {
      fetchEmployees();
    }
  }, [sessionToken]);

  useEffect(() => {
    if (sessionToken && employees.length > 0) {
      // Check if date is provided in query params
      const dateParam = searchParams.get('date');
      if (dateParam) {
        setCurrentDate(dateParam);
        fetchBalanceData(dateParam);
      } else {
        fetchCurrentDate().then(date => {
          if (date) {
            fetchBalanceData(date);
          }
        });
      }
    }
  }, [sessionToken, employees.length, searchParams]);

  // Add row handlers
  const handleAddAmRow = () => {
    if (!amForm.employeeId || !amForm.cashDue || !amForm.netSales) return;
    
    const employee = employees.find(e => e.id === Number(amForm.employeeId));
    const newRow: BalanceRow = {
      id: `am-${Date.now()}`,
      employeeId: Number(amForm.employeeId),
      employeeName: employee?.name || `Employee ${amForm.employeeId}`,
      type: amForm.type,
      cashDue: parseFloat(amForm.cashDue),
      netSales: parseFloat(amForm.netSales),
    };
    
    setAmData([...amData, newRow]);
    setAmForm({ employeeId: '', type: 'server', cashDue: '', netSales: '' });
  };

  const handleAddPmRow = () => {
    if (!pmForm.employeeId || !pmForm.cashDue || !pmForm.netSales) return;
    
    const employee = employees.find(e => e.id === Number(pmForm.employeeId));
    const newRow: BalanceRow = {
      id: `pm-${Date.now()}`,
      employeeId: Number(pmForm.employeeId),
      employeeName: employee?.name || `Employee ${pmForm.employeeId}`,
      type: pmForm.type,
      cashDue: parseFloat(pmForm.cashDue),
      netSales: parseFloat(pmForm.netSales),
    };
    
    setPmData([...pmData, newRow]);
    setPmForm({ employeeId: '', type: 'server', cashDue: '', netSales: '' });
  };

  // Edit handlers for AM/PM
  const handleStartEditAm = (row: BalanceRow) => {
    setEditingAmRow(row.id);
    setTempAmData({ ...row });
  };

  const handleStartEditPm = (row: BalanceRow) => {
    setEditingPmRow(row.id);
    setTempPmData({ ...row });
  };

  const handleSaveEditAm = () => {
    if (!editingAmRow || !tempAmData) return;
    setAmData(amData.map(row => row.id === editingAmRow ? { ...tempAmData as BalanceRow, id: row.id } : row));
    setEditingAmRow(null);
    setTempAmData(null);
  };

  const handleSaveEditPm = () => {
    if (!editingPmRow || !tempPmData) return;
    setPmData(pmData.map(row => row.id === editingPmRow ? { ...tempPmData as BalanceRow, id: row.id } : row));
    setEditingPmRow(null);
    setTempPmData(null);
  };

  const handleCancelEditAm = () => {
    setEditingAmRow(null);
    setTempAmData(null);
  };

  const handleCancelEditPm = () => {
    setEditingPmRow(null);
    setTempPmData(null);
  };

  // Delete handlers
  const handleDeleteAmRow = (id: string) => {
    setAmData(amData.filter(row => row.id !== id));
  };

  const handleDeletePmRow = (id: string) => {
    setPmData(pmData.filter(row => row.id !== id));
  };

  // Calculate totals
  const calculateTotals = () => {
    const amCashDue = amData.reduce((sum, row) => sum + (Number(row.cashDue) || 0), 0);
    const pmCashDue = pmData.reduce((sum, row) => sum + (Number(row.cashDue) || 0), 0);
    const paidOutsCashDue = paidOuts.reduce((sum, entry) => sum + (Number(entry.cashDue) || 0), 0);
    const totalCashDue = Number(amCashDue) + Number(pmCashDue) + Number(paidOutsCashDue);
    
    const amNetSales = amData.reduce((sum, row) => sum + (Number(row.netSales) || 0), 0);
    const pmNetSales = pmData.reduce((sum, row) => sum + (Number(row.netSales) || 0), 0);
    const totalNetSales = Number(amNetSales) + Number(pmNetSales);
    
    return { 
      totalCashDue: Number(totalCashDue) || 0, 
      totalNetSales: Number(totalNetSales) || 0 
    };
  };

  // Save data
  // Shared save path for both the manual "Save" button and auto-save. `silent`
  // suppresses toasts/errors in favor of the auto-save status indicator.
  const saveBalanceData = async (silent: boolean): Promise<void> => {
    if (!sessionToken || !currentDate) {
      if (!silent) showError('Missing session token or date');
      return;
    }

    try {
      const balanceData = [
        ...amData.map(row => ({
          employeeId: Number(row.employeeId) || 0,
          type: row.type,
          cashDue: Number(row.cashDue) || 0,
          shift: 'AM' as const,
          netSales: Number(row.netSales) || 0,
        })),
        ...pmData.map(row => ({
          employeeId: Number(row.employeeId) || 0,
          type: row.type,
          cashDue: Number(row.cashDue) || 0,
          shift: 'PM' as const,
          netSales: Number(row.netSales) || 0,
        })),
      ];

      const requestData = {
        date: currentDate,
        balance: balanceData,
        paid_outs: paidOuts.map(entry => ({
          description: entry.description,
          cashDue: Number(entry.cashDue) || 0,
        })),
        busser_tips: busserTips.map(entry => ({
          busserName: entry.busserName,
          tips: Number(entry.tips) || 0,
        })),
        event_servers_bartender_tips: eventServersBartenderTips.map(entry => ({
          eventServerBartender: entry.eventServerBartender,
          pooledTips: Number(entry.pooledTips) || 0,
        })),
        negative_cash_outs: negativeCashOuts.map(entry => ({
          tookFrom: entry.tookFrom,
          negativeCashOuts: Number(entry.negativeCashOuts) || 0,
        })),
        // Retired: Keno Cash tracking is now handled by the Register section's
        // "Keno" row, so this always saves empty (the backend field is still
        // required, but nothing populates or reads it on this page any more).
        keno_cash: [],
        register: REGISTER_DEFAULTS.map(r => ({
          registerType: r.key,
          employeeId: registerEmployeeForm[r.key] ? Number(registerEmployeeForm[r.key]) : null,
          closeOutAmount: parseFloat(registerForm[r.key]) || 0,
        })),
        bar_tips: barTips.map(entry => ({
          bartenderName: entry.bartenderName,
          tipAmount: Number(entry.tipAmount) || 0,
        })),
        expo_tips: expoTips.map(entry => ({
          expoName: entry.expoName,
          tipAmount: Number(entry.tipAmount) || 0,
        })),
      };

      await balanceApi.saveBalance(sessionToken, requestData);

      if (silent) {
        // Don't reload here - the fetch would race the next debounced auto-save if the
        // user keeps editing, and the data we just sent is already the latest state.
        setAutoSaveStatus('saved');
      } else {
        showSuccess('Balance data saved successfully');
        fetchBalanceData(currentDate);
      }
    } catch (error) {
      console.error('Error saving balance data:', error);
      if (silent) {
        setAutoSaveStatus('error');
        setAutoSaveError(error instanceof Error ? error.message : 'Failed to save changes');
      } else {
        showError('Failed to save balance data');
      }
    }
  };

  const performAutoSave = useCallback(async () => {
    setAutoSaveStatus('saving');
    await saveBalanceData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amData, pmData, paidOuts, busserTips, eventServersBartenderTips, negativeCashOuts, registerForm, registerEmployeeForm, barTips, expoTips, sessionToken, currentDate]);

  // Debounce a bulk auto-save ~900ms after any add/edit/delete across any of the
  // nine sections - but not the initial load from fetchBalanceData.
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setAutoSaveStatus('pending');
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      performAutoSave();
    }, 900);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amData, pmData, paidOuts, busserTips, eventServersBartenderTips, negativeCashOuts, registerForm, registerEmployeeForm, barTips, expoTips]);

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      await saveBalanceData(false);
    } finally {
      setSaveLoading(false);
    }
  };

  // Render table with sections
  const ROLE_LABELS: Record<BalanceRow['type'], string> = {
    server: 'Server',
    bar: 'Bar',
    host: 'Host',
    manager: 'Manager',
  };

  const renderBalanceTable = (
    data: BalanceRow[],
    shift: 'AM' | 'PM',
    onAdd: () => void,
    onDelete: (id: string) => void,
    form: any,
    setForm: any,
    editingRow: string | null,
    tempData: Partial<BalanceRow> | null,
    onStartEdit: (row: BalanceRow) => void,
    onSaveEdit: () => void,
    onCancelEdit: () => void,
    onTempDataChange: (field: string, value: any) => void
  ) => {
    const sections: Array<{ label: string; rows: BalanceRow[] }> = [
      { label: 'Servers', rows: data.filter(row => row.type === 'server') },
      { label: 'Bar', rows: data.filter(row => row.type === 'bar') },
      { label: 'Host', rows: data.filter(row => row.type === 'host') },
      { label: 'Manager', rows: data.filter(row => row.type === 'manager') },
    ];

    const subtotalCashDue = data.reduce((sum, row) => sum + (Number(row.cashDue) || 0), 0);
    const subtotalNetSales = data.reduce((sum, row) => sum + (Number(row.netSales) || 0), 0);

    const hdrCell = {
      color: '#fff', fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.04em',
      textTransform: 'uppercase' as const, border: '2px solid #7f0000', py: 0.75, px: 1.5,
    };
    const secHdrCell = { border: '2px solid #9e9e9e', py: 0.4, px: 1.5, backgroundColor: '#c8e6c9' };

    const renderDataRow = (row: BalanceRow, idx: number) => {
      const isEditing = editingRow === row.id;
      const currentData = isEditing && tempData ? tempData : row;
      return (
        <TableRow key={row.id} sx={{ backgroundColor: idx % 2 === 1 ? '#f0f0f0' : 'inherit' }}>
          <TableCell>
            {isEditing ? (
              <FormControl size="small" sx={{ minWidth: 300 }}>
                <Select
                  value={currentData.employeeId?.toString() || ''}
                  onChange={(event) => {
                    const emp = employees.find(emp => emp.id === Number(event.target.value));
                    onTempDataChange('employeeId', Number(event.target.value));
                    onTempDataChange('employeeName', emp?.name || '');
                  }}
                  disabled={employeesLoading}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id.toString()}>
                      {emp.name} ({emp.employeeId || emp.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography component="span" sx={{ fontWeight: 700 }}>
                {row.employeeName}
              </Typography>
            )}
          </TableCell>
          <TableCell>{ROLE_LABELS[row.type]}</TableCell>
          <TableCell align="right">
            {isEditing ? (
              <TextField
                size="small"
                type="number"
                value={currentData.cashDue || ''}
                onChange={(e) => onTempDataChange('cashDue', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.01 }}
                sx={{ width: 120 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                }}
              />
            ) : (
              `$${(Number(row.cashDue) || 0).toFixed(2)}`
            )}
          </TableCell>
          <TableCell align="right">
            {isEditing ? (
              <TextField
                size="small"
                type="number"
                value={currentData.netSales || ''}
                onChange={(e) => onTempDataChange('netSales', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.01 }}
                sx={{ width: 120 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                }}
              />
            ) : (
              `$${(Number(row.netSales) || 0).toFixed(2)}`
            )}
          </TableCell>
          <TableCell align="center">
            {isEditing ? (
              <>
                <IconButton size="small" onClick={onSaveEdit} color="success">
                  <SaveIcon />
                </IconButton>
                <IconButton size="small" onClick={onCancelEdit} color="error">
                  <CancelIcon />
                </IconButton>
              </>
            ) : (
              <>
                <IconButton size="small" onClick={() => onStartEdit(row)} color="primary">
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(row.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </>
            )}
          </TableCell>
        </TableRow>
      );
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            {shift}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flex: '0 0 auto' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleDateNav('prev')}
              disabled={dateLoading || dataLoading}
              sx={{ fontWeight: 700, color: '#2e7d32', borderColor: '#2e7d32', '&:hover': { borderColor: '#1b5e20', backgroundColor: 'rgba(46, 125, 50, 0.08)' } }}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleDateNav('next')}
              disabled={dateLoading || dataLoading}
              sx={{ fontWeight: 700, color: '#2e7d32', borderColor: '#2e7d32', '&:hover': { borderColor: '#1b5e20', backgroundColor: 'rgba(46, 125, 50, 0.08)' } }}
            >
              Next
            </Button>
          </Box>
        </Box>
        <TableContainer component={Paper} sx={{ border: '2px solid #9e9e9e' }}>
          <Table size="small" sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 0.5, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                <TableCell sx={hdrCell}>Employee</TableCell>
                <TableCell sx={hdrCell}>Role</TableCell>
                <TableCell align="right" sx={hdrCell}>Cash Due</TableCell>
                <TableCell align="right" sx={hdrCell}>Net Sales</TableCell>
                <TableCell align="center" sx={hdrCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.map((section) => section.rows.length > 0 && (
                <React.Fragment key={section.label}>
                  <TableRow>
                    <TableCell colSpan={5} sx={secHdrCell}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '18px', color: '#000080' }}>{section.label}</Typography>
                    </TableCell>
                  </TableRow>
                  {section.rows.map(renderDataRow)}
                </React.Fragment>
              ))}

              {/* Add Form Row */}
              <TableRow>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel>Employee</InputLabel>
                    <Select
                      value={form.employeeId}
                      label="Employee"
                      onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                      disabled={employeesLoading}
                    >
                      {employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id.toString()}>
                          {emp.name} ({emp.employeeId || emp.id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value })}
                    >
                      <MenuItem value="server">Server</MenuItem>
                      <MenuItem value="bar">Bar</MenuItem>
                      <MenuItem value="host">Host</MenuItem>
                      <MenuItem value="manager">Manager</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Cash Due"
                    value={form.cashDue}
                    onChange={(e) => setForm({ ...form, cashDue: e.target.value })}
                    inputProps={{ step: 0.01 }}
                    sx={{ width: 120 }}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Net Sales"
                    value={form.netSales}
                    onChange={(e) => setForm({ ...form, netSales: e.target.value })}
                    inputProps={{ step: 0.01 }}
                    sx={{ width: 120 }}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onAdd}
                    disabled={!form.employeeId || !form.cashDue || !form.netSales}
                  >
                    ADD
                  </Button>
                </TableCell>
              </TableRow>

              {/* Shift Subtotal */}
              <TableRow sx={{ backgroundColor: '#fde8e8', '& .MuiTableCell-root': { fontWeight: 700, color: '#7f0000', border: '2px solid #7f0000' } }}>
                <TableCell colSpan={2} align="right">{shift} Subtotal</TableCell>
                <TableCell align="right">${subtotalCashDue.toFixed(2)}</TableCell>
                <TableCell align="right">${subtotalNetSales.toFixed(2)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };


  const totals = calculateTotals();
  const registerSubtotal = REGISTER_DEFAULTS.reduce((sum, r) => sum + (parseFloat(registerForm[r.key]) || 0), 0);
  // Day Close out Amount - Default Amount; $0.00 when no close-out amount has been entered yet.
  const computeEndOfDayBalance = (r: { key: string; defaultAmount: number }): number => {
    const raw = registerForm[r.key];
    if (!raw || raw.trim() === '') return 0;
    const closeOut = parseFloat(raw);
    if (isNaN(closeOut)) return 0;
    return closeOut - r.defaultAmount;
  };
  const endOfDayBalanceSubtotal = REGISTER_DEFAULTS.reduce((sum, r) => sum + computeEndOfDayBalance(r), 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Date and Save Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {dateLoading ? (
            <CircularProgress size={16} sx={{ ml: 1 }} />
          ) : currentDate ? (
            formatDateString(currentDate)
          ) : (
            new Date().toLocaleDateString('en-US', {
              timeZone: 'America/New_York',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          )}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          New Balance Form
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 0 auto' }}>
          <Typography
            variant="body2"
            color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
            {autoSaveStatus === 'pending' && 'Unsaved changes…'}
            {autoSaveStatus === 'saved' && 'All changes saved'}
            {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={saveLoading}
            startIcon={saveLoading ? undefined : <SaveIcon />}
            sx={{ minWidth: 150 }}
          >
            {saveLoading ? <CircularProgress size={24} /> : 'SAVE DAILY'}
          </Button>
        </Box>
      </Box>

      {dataLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!dataLoading && (
        <>
          {/* AM Table */}
          {renderBalanceTable(
            amData, 
            'AM', 
            handleAddAmRow, 
            handleDeleteAmRow, 
            amForm, 
            setAmForm,
            editingAmRow,
            tempAmData,
            handleStartEditAm,
            handleSaveEditAm,
            handleCancelEditAm,
            (field: string, value: any) => setTempAmData({ ...tempAmData, [field]: value } as Partial<BalanceRow>)
          )}

          {/* PM Table */}
          {renderBalanceTable(
            pmData, 
            'PM', 
            handleAddPmRow, 
            handleDeletePmRow, 
            pmForm, 
            setPmForm,
            editingPmRow,
            tempPmData,
            handleStartEditPm,
            handleSaveEditPm,
            handleCancelEditPm,
            (field: string, value: any) => setTempPmData({ ...tempPmData, [field]: value } as Partial<BalanceRow>)
          )}

          {/* Register */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ mb: 3, flex: '1 1 0', minWidth: 480 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                  Cash Registers
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flex: '0 0 auto' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDateNav('prev')}
                    disabled={dateLoading || dataLoading}
                    sx={{ fontWeight: 700, color: '#2e7d32', borderColor: '#2e7d32', '&:hover': { borderColor: '#1b5e20', backgroundColor: 'rgba(46, 125, 50, 0.08)' } }}
                  >
                    Prev
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDateNav('next')}
                    disabled={dateLoading || dataLoading}
                    sx={{ fontWeight: 700, color: '#2e7d32', borderColor: '#2e7d32', '&:hover': { borderColor: '#1b5e20', backgroundColor: 'rgba(46, 125, 50, 0.08)' } }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
              <TableContainer component={Paper} sx={{ border: '2px solid #9e9e9e', width: '100%' }}>
                <Table size="small" sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', '& td': { border: '2px solid #9e9e9e', py: 0.5, px: 1 } }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                      {['Register', 'Employee', 'Default Amount', 'Day Close out Amount', 'End of the Day Balance'].map((h, i) => (
                        <TableCell key={h} align={i > 1 ? 'right' : 'left'} sx={{
                          color: '#fff', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.03em',
                          textTransform: 'uppercase', border: '2px solid #7f0000', py: 0.75, px: 1,
                          width: ['10%', '44%', '11%', '18%', '17%'][i],
                        }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {REGISTER_DEFAULTS.map((r) => (
                      <TableRow key={r.key}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{r.label}</TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={registerEmployeeForm[r.key] || ''}
                              onChange={(e) => setRegisterEmployeeForm({ ...registerEmployeeForm, [r.key]: e.target.value })}
                              displayEmpty
                              disabled={employeesLoading}
                              sx={{ fontSize: '0.8rem' }}
                            >
                              <MenuItem value=""><em>—</em></MenuItem>
                              {employees.map((emp) => (
                                <MenuItem key={emp.id} value={emp.id.toString()} sx={{ fontSize: '0.8rem' }}>
                                  {emp.name} ({emp.employeeId || emp.id})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>${r.defaultAmount.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={registerForm[r.key] || ''}
                            onChange={(e) => setRegisterForm({ ...registerForm, [r.key]: e.target.value })}
                            inputProps={{ step: 0.01 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 0.25, fontSize: '0.8rem' }}>$</Typography>
                            }}
                            sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          ${computeEndOfDayBalance(r).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: '#fde8e8', '& .MuiTableCell-root': { fontWeight: 700, color: '#7f0000', border: '2px solid #7f0000' } }}>
                      <TableCell colSpan={3} align="right">Subtotal</TableCell>
                      <TableCell align="right">${registerSubtotal.toFixed(2)}</TableCell>
                      <TableCell align="right">${endOfDayBalanceSubtotal.toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* Totals Row */}
          <TableContainer component={Paper} sx={{ mb: 3, border: '2px solid #7f0000' }}>
            <Table sx={{ borderCollapse: 'collapse' }}>
              <TableBody>
                <TableRow sx={{ backgroundColor: '#b71c1c', '& .MuiTableCell-root': { color: 'white', fontWeight: 'bold', border: '2px solid #7f0000', py: 1.25, px: 1.5 } }}>
                  <TableCell>TOTAL</TableCell>
                  <TableCell align="right">${totals.totalCashDue.toFixed(2)}</TableCell>
                  <TableCell align="right">{totals.totalNetSales.toFixed(2)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

        </>
      )}

      {/* Net Sales mismatch popup */}
      <Dialog open={netSalesMismatchMessage !== null} onClose={() => setNetSalesMismatchMessage(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#7f0000', fontWeight: 700 }}>Net Sales Mismatch</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{netSalesMismatchMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNetSalesMismatchMessage(null)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewBalancePage;
